import { type FC } from 'react';

import Section from 'molecules/Section';
import { splitSectionProps } from 'molecules/Section/config';

import {
  DEFAULT_SECTIONS,
  QUIZ_QUESTIONS,
  calculateSectionCounts,
  getSectionDisplayName,
  getUniqueSections,
} from 'components/BuildVsBuyQuiz/config/questions';
import { useQuizState } from 'components/BuildVsBuyQuiz/hooks/useQuizState';

import type { BuildVsBuyQuizProps } from 'components/BuildVsBuyQuiz/config';
import type { QuizQuestion, Section as SectionType } from 'components/BuildVsBuyQuiz/config/types';

/**
 * BuildVsBuyQuiz Component
 *
 * FULLY DYNAMIC QUIZ SYSTEM:
 * - Works with ANY number of questions
 * - Supports ANY section names via sections list
 * - Automatic scoring adjustment
 * - Dynamic progress bars for all sections
 * - Result-specific buttons configurable per bucket
 *
 * SECTIONS MANAGEMENT:
 * - Sections defined in separate list with ID + displayName
 * - Questions reference section IDs
 * - One source of truth for section names
 * - Easy to add/remove/rename sections
 *
 * RESULT BUTTONS:
 * - Each result bucket has its own button array
 * - Buttons show with correct timing per variant
 * - Fully configurable from Builder.io
 */
const BuildVsBuyQuiz: FC<BuildVsBuyQuizProps> = props => {
  const {
    sectionProps,
    rest: {
      variant = 'gated_start',
      quizVersion = 'v1',
      redirectUrl = '/results-build-vs-buy-quiz',
      sections = DEFAULT_SECTIONS,
      questions = QUIZ_QUESTIONS,
      clearBuyHeading,
      clearBuyDescription,
      clearBuyButtons,
      leansBuyHeading,
      leansBuyDescription,
      leansBuyButtons,
      tossUpHeading,
      tossUpDescription,
      tossUpButtons,
      leansBuildHeading,
      leansBuildDescription,
      leansBuildButtons,
      clearBuildHeading,
      clearBuildDescription,
      clearBuildButtons,
    },
  } = splitSectionProps(props);

  // Cast to proper types
  const quizSections = sections as SectionType[];
  const quizQuestions = questions as QuizQuestion[];

  // Quiz state and handlers
  const { currentStep, answers, isTransitioning, isHydrating, isCompletingQuiz, handleAnswer, handleBack } =
    useQuizState({
      variant,
      quizVersion,
      redirectUrl,
      questions: quizQuestions,
      clearBuyHeading,
      clearBuyDescription,
      clearBuyButtons,
      leansBuyHeading,
      leansBuyDescription,
      leansBuyButtons,
      tossUpHeading,
      tossUpDescription,
      tossUpButtons,
      leansBuildHeading,
      leansBuildDescription,
      leansBuildButtons,
      clearBuildHeading,
      clearBuildDescription,
      clearBuildButtons,
    });

  /**
   * Render loading state
   */
  const renderLoadingState = () => (
    <div className="flex min-h-[600px] items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="size-12 animate-spin rounded-full border-4 border-gray-600 border-t-primary-500" />
      </div>
    </div>
  );

  /**
   * Render question with DYNAMIC progress sidebar
   */
  const renderQuestion = (questionIndex: number) => {
    const question = quizQuestions[questionIndex];
    const currentAnswer = answers[questionIndex];

    // Get all unique sections from questions
    const uniqueSections = getUniqueSections(quizQuestions);
    const sectionCounts = calculateSectionCounts(quizQuestions);

    // Calculate progress for each section dynamically
    const sectionProgress: Record<string, number> = {};
    uniqueSections.forEach(section => {
      const answeredInSection = Object.keys(answers).filter(k => quizQuestions[Number(k)].section === section).length;
      const totalInSection = sectionCounts[section] || 1;
      sectionProgress[section] = (answeredInSection / totalInSection) * 100;
    });

    // Normalize button order: always show Yes/positive answer first
    const normalizeLabels = (blueLabel: string, orangeLabel: string) => {
      const blueLower = blueLabel.toLowerCase().trim();
      const orangeLower = orangeLabel.toLowerCase().trim();

      // Check if we need to swap (if orange is "yes" or blue is "no")
      const shouldSwap = orangeLower === 'yes' || orangeLower === 'sí' || orangeLower === 'si' || blueLower === 'no';

      if (shouldSwap) {
        return {
          firstLabel: orangeLabel,
          firstValue: 'orange' as const,
          secondLabel: blueLabel,
          secondValue: 'blue' as const,
        };
      }

      return {
        firstLabel: blueLabel,
        firstValue: 'blue' as const,
        secondLabel: orangeLabel,
        secondValue: 'orange' as const,
      };
    };

    const { firstLabel, firstValue, secondLabel, secondValue } = normalizeLabels(
      question.blueLabel,
      question.orangeLabel,
    );

    return (
      <div className="flex flex-col gap-12 lg:flex-row lg:gap-12 xl:gap-48">
        {/* Question Content */}
        <div
          className={`flex flex-col gap-12 transition-opacity duration-300 lg:max-w-xl lg:flex-1 ${
            isTransitioning ? 'opacity-0' : 'opacity-100'
          }`}
        >
          {/* Breadcrumb */}
          <div className="flex items-center gap-2.5">
            <span className="text-xs text-gray-100">Build vs Buy Quiz</span>
            <svg className="size-4" viewBox="0 0 16 16" fill="none">
              <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" className="text-gray-400" />
            </svg>
            <span className="text-xs font-medium text-white">
              {getSectionDisplayName(question.section, quizSections)}
            </span>
          </div>

          {/* Question */}
          <h2 className="text-3xl leading-9 text-gray-200 lg:text-4xl lg:leading-10">{question.question}</h2>

          {/* Answer Buttons - Normalized order: Yes/positive always first */}
          <div className="flex flex-col gap-4 sm:flex-row sm:gap-4">
            <button
              onClick={() => handleAnswer(questionIndex, firstValue)}
              disabled={isTransitioning}
              className={`w-full rounded-lg px-6 py-3 font-semibold transition-colors disabled:cursor-not-allowed sm:w-64 ${
                currentAnswer === firstValue ? 'bg-primary-500 text-white' : 'bg-gray-700 text-white hover:bg-gray-600'
              }`}
            >
              {firstLabel}
            </button>
            <button
              onClick={() => handleAnswer(questionIndex, secondValue)}
              disabled={isTransitioning}
              className={`w-full rounded-lg px-6 py-3 font-semibold transition-colors disabled:cursor-not-allowed sm:w-64 ${
                currentAnswer === secondValue ? 'bg-primary-500 text-white' : 'bg-gray-700 text-white hover:bg-gray-600'
              }`}
            >
              {secondLabel}
            </button>
          </div>

          {/* Back Button */}
          {currentStep > 1 && (
            <button
              onClick={handleBack}
              disabled={isTransitioning}
              className="flex items-center gap-2 text-gray-100 transition-colors hover:text-white disabled:cursor-not-allowed"
            >
              <svg className="size-6 rotate-180" viewBox="0 0 24 24" fill="none">
                <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" />
              </svg>
              Back
            </button>
          )}
        </div>

        {/* DYNAMIC Progress Sidebar */}
        <div className="hidden flex-col gap-8 md:flex lg:w-80 lg:shrink-0">
          <div className="font-medium text-white">Progress</div>

          {/* Render progress bar for each section dynamically */}
          {uniqueSections.map(section => (
            <div key={section} className="flex flex-col gap-3">
              <div className="text-gray-100">{getSectionDisplayName(section, quizSections)}</div>
              <div className="h-[3px] w-full rounded-[3px] bg-gray-600">
                <div
                  className="h-full rounded-[3px] bg-primary-300 transition-all duration-300"
                  style={{ width: `${sectionProgress[section]}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  /**
   * Main render logic
   */
  const renderContent = () => {
    if (isHydrating) {
      return renderLoadingState();
    }

    if (isCompletingQuiz) {
      return renderLoadingState();
    }

    const questionIndex = currentStep - 1;

    if (questionIndex >= 0 && questionIndex < quizQuestions.length) {
      return renderQuestion(questionIndex);
    }

    return renderLoadingState();
  };

  return (
    <Section {...sectionProps}>
      <div className="min-h-[600px]">{renderContent()}</div>
    </Section>
  );
};

export default BuildVsBuyQuiz;
